/** Helpers de presentación — detalle Centro SaaS solicitudes de licencia. */
import {
  scopeRangeLabelClients,
  scopeRangeLabelUsers,
} from "@/lib/commercialLicenseScope";

export const EXPANSION_REQUEST_KINDS = new Set([
  "renewal_or_expansion",
  "renewal",
  "expansion",
  "user_limit_increase",
  "client_limit_increase",
  "module_upgrade",
  "mixed_update",
  "special_adjustment",
]);

export function isExpansionRequestKind(kind) {
  return EXPANSION_REQUEST_KINDS.has(String(kind || "").trim().toLowerCase());
}

export function safeDisplayValue(value, fallback = "No especificado") {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

const REQUEST_KIND_LABELS = {
  initial: "Solicitud inicial",
  renewal_or_expansion: "Renovación o ampliación",
  renewal: "Renovación de vigencia",
  expansion: "Ampliación",
  user_limit_increase: "Aumento de usuarios",
  client_limit_increase: "Aumento de clientes",
  module_upgrade: "Agregar módulos",
  mixed_update: "Ampliación mixta",
  special_adjustment: "Ajuste especial",
};

export function requestKindLabel(kind) {
  const k = String(kind || "").trim().toLowerCase();
  return REQUEST_KIND_LABELS[k] || safeDisplayValue(kind, "—");
}

const HISTORY_EVENT_LABELS = {
  submitted: "Solicitud recibida",
  resubmitted: "Solicitud actualizada",
  reminder_requested: "Aviso reenviado a BNT",
  payment_proof_attached: "Comprobante anexado",
  payment_validated: "Comprobante validado",
  payment_rejected: "Comprobante rechazado",
  fulfill_modules_adjusted: "Ajuste de módulos autorizados",
  fulfill_scope_adjusted: "Ajuste de alcance autorizado",
  license_issued: "Licencia emitida",
  status_in_review: "Marcada en revisión",
  status_approved: "Aprobada",
  status_rejected: "Rechazada",
};

/** @param {string} event */
export function requestHistoryEventLabel(event) {
  const key = String(event || "").trim().toLowerCase();
  if (HISTORY_EVENT_LABELS[key]) return HISTORY_EVENT_LABELS[key];
  if (key.startsWith("status_")) {
    return `Estado: ${key.replace("status_", "").replace(/_/g, " ")}`;
  }
  return safeDisplayValue(event, "Evento");
}

/** @param {object} row */
export function formatScopeSummary(row = {}) {
  const users = row.estimated_users ?? "—";
  const clients = row.estimated_clients ?? "—";
  const usersRange = row.estimated_users_range
    ? scopeRangeLabelUsers(row.estimated_users_range)
    : "";
  const clientsRange = row.estimated_clients_range
    ? scopeRangeLabelClients(row.estimated_clients_range)
    : "";
  const uPart = usersRange ? `${users} (${usersRange})` : String(users);
  const cPart = clientsRange ? `${clients} (${clientsRange})` : String(clients);
  return `${uPart} usuarios · ${cPart} clientes`;
}

/** @param {object} row */
export function formatExpansionLimitsSummary(row = {}) {
  const parts = [];
  const curU = row.current_authorized_users;
  const reqU = row.requested_authorized_users;
  const curC = row.current_authorized_clients;
  const reqC = row.requested_authorized_clients;
  if (curU != null || reqU != null) {
    parts.push(`Usuarios: ${curU ?? "—"} → ${reqU ?? "—"}`);
  }
  if (curC != null || reqC != null) {
    parts.push(`Clientes: ${curC ?? "—"} → ${reqC ?? "—"}`);
  }
  if (row.current_valid_until || row.requested_valid_until) {
    parts.push(
      `Vigencia: ${row.current_valid_until || "—"} → ${row.requested_valid_until || "—"}`,
    );
  }
  return parts.length ? parts.join(" · ") : "—";
}

export function paymentValidationStatusLabel(status) {
  const st = String(status || "").trim().toLowerCase();
  const labels = {
    not_provided: "Sin comprobante",
    pending: "Comprobante pendiente de validar",
    validated: "Pago validado",
    rejected: "Comprobante rechazado",
  };
  return labels[st] || safeDisplayValue(status, "Sin comprobante");
}
